import { useState, useEffect } from 'react';
import type { LeaderboardEntry } from '@tinymint/shared';

interface LeaderboardProps {
  gameId: string;
  serverId: string;
  currentUserId?: string;
  onClose?: () => void;
}

function Leaderboard({ gameId, serverId, currentUserId, onClose }: LeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [userEntry, setUserEntry] = useState<LeaderboardEntry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [gameId, serverId]);

  const fetchLeaderboard = async () => {
    try {
      const params = new URLSearchParams({
        server: serverId,
        limit: '10',
      });
      if (currentUserId) {
        params.set('user', currentUserId);
      }

      const response = await fetch(`/.proxy/api/scores/${gameId}?${params}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.leaderboard);
        setUserEntry(data.userEntry);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankClass = (rank: number) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return '';
  };

  const getRankEmoji = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (loading) {
    return (
      <div className="leaderboard">
        <div className="loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard">
      <div className="leaderboard-title">
        <span>🏆</span>
        <span>Server Leaderboard</span>
        {onClose && (
          <button
            className="nav-btn"
            style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem' }}
            onClick={onClose}
          >
            ✕
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '1rem' }}>
          No scores yet. Be the first!
        </p>
      ) : (
        <>
          {entries.map((entry) => (
            <div
              key={entry.user_id}
              className={`leaderboard-entry ${entry.is_current_user ? 'current-user' : ''}`}
            >
              <span className={`leaderboard-rank ${getRankClass(entry.rank)}`}>
                {getRankEmoji(entry.rank)}
              </span>
              <span className="leaderboard-username">
                {entry.username}
                {entry.is_current_user && ' (You)'}
              </span>
              <span className="leaderboard-score">
                {entry.score.toLocaleString()}
              </span>
            </div>
          ))}

          {/* Show user's rank if not in top 10 */}
          {userEntry && !entries.some(e => e.is_current_user) && (
            <>
              <div style={{ textAlign: 'center', padding: '0.5rem', color: 'var(--text-secondary)' }}>
                ⋮
              </div>
              <div className="leaderboard-entry current-user">
                <span className="leaderboard-rank">#{userEntry.rank}</span>
                <span className="leaderboard-username">You</span>
                <span className="leaderboard-score">
                  {userEntry.score.toLocaleString()}
                </span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Leaderboard;
